import { Chess, DEFAULT_POSITION, type Square } from "chess.js";
import type { AppState, Color, Game, Variation } from "./types";
export { DEFAULT_POSITION };
export const opposite = (color: Color): Color => (color === "w" ? "b" : "w");
export const colorName = (color: Color) => (color === "w" ? "White" : "Black");
export function replay(
  game: Pick<Game | Variation, "initialFen" | "moves">,
  ply = game.moves.length,
): Chess {
  const chess = new Chess(game.initialFen);
  for (const move of game.moves.slice(0, ply)) chess.move(move);
  return chess;
}
export function activeContext(state: AppState) {
  const game = state.games.find((g) => g.id === state.activeGameId);
  const variation = state.variations.find(
    (v) => v.id === state.activeVariationId && v.gameId === game?.id,
  );
  const record = variation ?? game;
  const chess = record
    ? replay(record, state.viewPly ?? record.moves.length)
    : new Chess();
  return { game, variation, record, chess };
}
export const uci = (move: { from: string; to: string; promotion?: string }) =>
  move.from + move.to + (move.promotion ?? "");
export function resolveMove(chess: Chess, input: string): string {
  const clean = input
    .trim()
    .replace(/[.!?]$/, "")
    .replace(/\s+/g, " ");
  try {
    const copy = new Chess(chess.fen());
    const move = copy.move(clean);
    return uci(move);
  } catch {
    /* Try spoken notation against legal moves. */
  }
  const pieces: Record<string, string> = {
    pawn: "p",
    knight: "n",
    horse: "n",
    bishop: "b",
    rook: "r",
    queen: "q",
    king: "k",
  };
  const normalized = clean
    .toLowerCase()
    .replace(/^move\s+/, "")
    .replace(/castle queen\s?side/, "O-O-O")
    .replace(/castle king\s?side|castle$/, "O-O");
  if (normalized === "O-O" || normalized === "O-O-O") {
    const move = chess
      .moves({ verbose: true })
      .find((m) => m.san === normalized);
    if (move) return uci(move);
  }
  const squares = normalized.match(/[a-h][1-8]/g) ?? [];
  const pieceWord = Object.keys(pieces).find((p) =>
    new RegExp(`\\b${p}\\b`).test(normalized),
  );
  const promotion = normalized.match(
    /promot(?:e|ion)(?: to)? (?:a )?(queen|rook|bishop|knight)/,
  )?.[1];
  const candidates = chess
    .moves({ verbose: true })
    .filter(
      (m) =>
        squares.length > 0 &&
        m.to === squares.at(-1) &&
        (squares.length < 2 || m.from === squares[0]) &&
        (!pieceWord || m.piece === pieces[pieceWord]) &&
        (!promotion || m.promotion === pieces[promotion]),
    );
  if (candidates.length === 1) return uci(candidates[0]);
  if (candidates.length > 1)
    throw new Error(
      `That move is ambiguous. Choose ${candidates.map((m) => m.san).join(" or ")}.`,
    );
  throw new Error(`“${input}” is not a legal move in this position.`);
}
export function positionStatus(chess: Chess) {
  if (chess.isCheckmate())
    return {
      ended: true,
      result: chess.turn() === "w" ? "0-1" : "1-0",
      label: `Checkmate · ${colorName(opposite(chess.turn()))} wins`,
    };
  if (chess.isStalemate())
    return { ended: true, result: "1/2-1/2", label: "Draw by stalemate" };
  if (chess.isInsufficientMaterial())
    return {
      ended: true,
      result: "1/2-1/2",
      label: "Draw · insufficient material",
    };
  const key = (fen: string) => fen.split(" ").slice(0, 4).join(" ");
  const history = chess.history({ verbose: true });
  const positions = history.length
    ? [history[0].before, ...history.map((m) => m.after)]
    : [chess.fen()];
  const repeats = positions.filter((p) => key(p) === key(chess.fen())).length;
  if (repeats >= 5)
    return {
      ended: true,
      result: "1/2-1/2",
      label: "Draw · fivefold repetition",
    };
  if (Number(chess.fen().split(" ")[4]) >= 150)
    return { ended: true, result: "1/2-1/2", label: "Draw · 75-move rule" };
  const claimable =
    chess.isThreefoldRepetition() || Number(chess.fen().split(" ")[4]) >= 100;
  return {
    ended: false,
    result: "*",
    label: `${colorName(chess.turn())} to move${chess.isCheck() ? " · in check" : ""}`,
    claimable,
  };
}
export function legalLine(fen: string, moves: string[]) {
  const chess = new Chess(fen);
  const legal: string[] = [];
  const san: string[] = [];
  for (const move of moves) {
    try {
      const m = chess.move(move);
      legal.push(uci(m));
      san.push(m.san);
    } catch {
      break;
    }
  }
  return { moves: legal, san };
}
export function gamePgn(game: Game): string {
  const chess = replay(game);
  for (const [key, value] of Object.entries(game.headers))
    chess.setHeader(key, value.replace(/[\r\n]/g, " "));
  chess.setHeader("Event", game.title);
  chess.setHeader("Result", game.result);
  return chess.pgn();
}
export function matchesGame(game: Game, filter: AppState["libraryFilter"]) {
  const blob =
    `${game.title} ${Object.values(game.headers).join(" ")} ${replay(game).history().join(" ")}`.toLowerCase();
  return (
    (!filter.query || blob.includes(filter.query.toLowerCase())) &&
    (!filter.result || game.result === filter.result) &&
    (!filter.color || game.humanColor === filter.color) &&
    (!filter.after || game.createdAt >= new Date(filter.after).getTime()) &&
    (!filter.before ||
      game.createdAt < new Date(filter.before).getTime() + 86400000) &&
    (!filter.opening ||
      `${game.headers.Opening ?? ""} ${game.headers.ECO ?? ""}`
        .toLowerCase()
        .includes(filter.opening.toLowerCase())) &&
    (!filter.opponent ||
      `${game.headers[game.humanColor === "w" ? "Black" : "White"] ?? ""}`
        .toLowerCase()
        .includes(filter.opponent.toLowerCase())) &&
    (!filter.timeControl ||
      (game.headers.TimeControl ?? "").includes(filter.timeControl))
  );
}
export function pieceDescription(chess: Chess, square: Square) {
  const p = chess.get(square);
  const names: Record<string, string> = {
    p: "pawn",
    n: "knight",
    b: "bishop",
    r: "rook",
    q: "queen",
    k: "king",
  };
  return p
    ? `${colorName(p.color)} ${names[p.type]} on ${square}`
    : `${square}, empty`;
}
